/**
 * Hook that watches for AI credit threshold events and shows toast notifications.
 * Subscribes to recent threshold events and displays toasts for new crossings.
 */

import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const THRESHOLD_MESSAGES: Record<
  number,
  { title: string; description: string; type: "info" | "warning" | "error" }
> = {
  75: {
    title: "AI Credits: 75% Used",
    description: "You've used 75% of your AI credits this month.",
    type: "info",
  },
  90: {
    title: "AI Credits: Almost Exhausted",
    description: "Warning: Only 10% of your AI credits remaining.",
    type: "warning",
  },
  100: {
    title: "AI Credits Exhausted",
    description: "AI credits exhausted. Upgrade to continue using AI features.",
    type: "error",
  },
};

export function useCreditThresholdToasts() {
  const events = useQuery(api.ai_credit_queries.getRecentThresholdEvents, {});
  const shownRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!events || events.length === 0) return;

    for (const event of events) {
      // Create a unique key per threshold+timestamp to avoid duplicate toasts
      const key = `${event.threshold}-${event.timestamp}`;
      if (shownRef.current.has(key)) continue;
      shownRef.current.add(key);

      const config = THRESHOLD_MESSAGES[event.threshold];
      if (!config) continue;

      if (config.type === "error") {
        toast.error(config.title, {
          description: config.description,
          duration: 8000,
        });
      } else if (config.type === "warning") {
        toast.warning(config.title, {
          description: config.description,
          duration: 6000,
        });
      } else {
        toast.info(config.title, {
          description: config.description,
          duration: 5000,
        });
      }
    }
  }, [events]);
}
